import type { LessonsBulkUpdatePayload, LessonsBulkUpdateResult } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface LessonsBulkUpdateApiOptions {
  disciplineId: string | number
  payload: LessonsBulkUpdatePayload
  onStart?: () => void
  onFinish?: () => void
}

/** `PATCH /admin-api/disciplines/{discipline}/lessons` — a 200 does not mean every lesson changed: the lessons that
 *  failed are named with their reason. A mixed reserved selection or a busy tutor refuses the whole request. */
export const useLessonsBulkUpdateApi = async (
  options: LessonsBulkUpdateApiOptions,
): Promise<LessonsBulkUpdateResult | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.lessons.api.index', { discipline_id: options.disciplineId })

  options.onStart?.()

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, options.payload)

    const result: LessonsBulkUpdateResult = {
      updated: data.data?.updated ?? [],
      failed: data.data?.failed ?? [],
    }

    if (result.updated.length) {
      toast.success({
        title: 'Successfully updated!',
        description: `Updated lessons: ${result.updated.length}.`,
      })
    }

    if (result.failed.length) {
      toast.warning({
        title: 'Some lessons were not updated',
        description: result.failed.map(item => `#${item.id}: ${item.message}`).join(' '),
      })
    }

    return result
  } catch (err: unknown) {
    toast.error({
      title: 'Error on update!',
      description: getAxiosErrorMessage(err),
    })

    return null
  } finally {
    options.onFinish?.()
  }
}
